import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit2, Trash2, RefreshCw } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import Loader from '../../components/ui/Loader';
import perfilService from '../../services/perfilService';

export default function Perfiles() {
  const [loading, setLoading] = useState(true);
  const [perfiles, setPerfiles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setPerfiles([]);

      // Cargar perfiles
      try {
        const perfilesData = await perfilService.getAllPerfiles();
        setPerfiles(perfilesData || []);
      } catch (error) {
        console.error('Error al cargar perfiles:', error);
        if (error.response?.status !== 400) {
          toast.error('Error al cargar perfiles');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data) => {
    try {
      if (editingItem) {
        await perfilService.updatePerfil(editingItem.id, data);
        toast.success('Perfil actualizado');
      } else {
        await perfilService.createPerfil(data);
        toast.success('Perfil creado');
      }
      loadData();
      closeModal();
    } catch (error) {
      toast.error('Error al guardar');
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este perfil?')) return;
    
    try {
      await perfilService.deletePerfil(id);
      toast.success('Perfil eliminado');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
      console.error(error);
    }
  };

  const openModal = (item = null) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const filteredPerfiles = perfiles.filter(p =>
    p.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.descripcion?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <Loader />;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-neutral-800">Perfiles</h1>
          <p className="text-neutral-600 mt-1">Gestión de perfiles de cargo</p>
        </div>
        <Button onClick={() => openModal()} className="gap-2">
          <Plus className="w-4 h-4" />
          Nuevo Perfil
        </Button>
      </div>

      <div className="flex justify-between items-center gap-4">
        <div className="flex-1 max-w-md">
          <Input
            placeholder="Buscar perfiles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={Search}
          />
        </div>
        <Button variant="outline" onClick={loadData} className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-neutral-50 border-b border-neutral-200">
            <tr>
              <th className="text-left p-4 text-sm font-semibold text-neutral-700">Nombre</th>
              <th className="text-left p-4 text-sm font-semibold text-neutral-700">Descripción</th>
              <th className="text-right p-4 text-sm font-semibold text-neutral-700">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200">
            {filteredPerfiles.length === 0 ? (
              <tr>
                <td colSpan="3" className="p-8 text-center text-neutral-500">
                  No se encontraron perfiles
                </td>
              </tr>
            ) : (
              filteredPerfiles.map((perfil) => (
                <tr key={perfil.id} className="hover:bg-neutral-50">
                  <td className="p-4 font-medium text-neutral-800">{perfil.nombre}</td>
                  <td className="p-4 text-neutral-600">{perfil.descripcion}</td>
                  <td className="p-4">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => openModal(perfil)}
                        className="p-2 text-primary hover:bg-primary-50 rounded-md transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(perfil.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <PerfilModal
        isOpen={showModal}
        onClose={closeModal}
        onSave={handleSave}
        initialData={editingItem}
      />
    </div>
  );
}

function PerfilModal({ isOpen, onClose, onSave, initialData }) {
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: ''
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        nombre: initialData.nombre || '',
        descripcion: initialData.descripcion || ''
      });
    } else {
      setFormData({ nombre: '', descripcion: '' });
    }
  }, [initialData, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={initialData ? 'Editar Perfil' : 'Nuevo Perfil'}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Nombre"
          value={formData.nombre}
          onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
          required
        />
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">
            Descripción
          </label>
          <textarea
            className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
            rows={3}
            value={formData.descripcion}
            onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
            required
          />
        </div>
        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit">
            Guardar
          </Button>
        </div>
      </form>
    </Modal>
  );
}
